import React, { useState, useEffect, useCallback } from 'react';
import processService from '../../services/process.service';
import templateService from '../../services/template.service';
import Swal from '../helpers/swal';
import { Link } from 'react-router-dom';
import { Collapse, Button, Pagination, Alert } from 'react-bootstrap';
import Loading from '../common/Loading';
import {
    CALL_STATUS_BADGES,
    formatStatusLabel,
    formatExtraInfo,
    formatScheduledSend,
    toProperCase,
    truncateText,
    canResendCallQueue,
} from './processQueueUtils';
import ProcessQueueFilters from './ProcessQueueFilters';

const SORT_FIELDS = [
    { value: 'dateSend', label: 'Scheduled Date' },
    { value: 'timeSend', label: 'Scheduled Time' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'category', label: 'Category' },
    { value: 'patientName', label: 'Patient Name' },
    { value: 'receiver', label: 'Receiver' },
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
];

const STATUS_OPTIONS = [
    'WAITING',
    'RESEND',
    'ACCEPTED',
    'QUEUED',
    'SENT',
    'FAILED',
    'DELIVERED',
    'UNDELIVERED',
    'ERROR',
    'REJECTED',
];

function CallProcessTable({
    searchPanelOpen = false,
    searchTerm = '',
    appliedSearch = '',
    onClearSearch,
}) {
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [sortBy, setSortBy] = useState('dateSend');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [statusFilter, setStatusFilter] = useState('');
    const [createdDate, setCreatedDate] = useState('');
    const [reminderType, setReminderType] = useState('');
    const [reminderTypeOptions, setReminderTypeOptions] = useState([]);

    const queryOptions = {
        sortBy,
        sortOrder,
        searchField: 'q',
        searchTerm: appliedSearch,
        statusFilter,
        createdDate,
        reminderType,
    };

    useEffect(() => {
        templateService
            .getTemplateTypes()
            .then((res) => {
                const options = (res.data || [])
                    .filter((type) => type?.isCall !== false && type?.reminderName)
                    .map((type) => ({
                        value: type.reminderName,
                        label: formatStatusLabel(type.reminderName),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                setReminderTypeOptions(options);
            })
            .catch(() => setReminderTypeOptions([]));
    }, []);

    const fetchList = useCallback(() => {
        setIsLoading(true);
        setError(null);
        processService
            .getAllCallQueue(currentPage, itemsPerPage, queryOptions)
            .then((res) => {
                setCurrentItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setSelectedItems([]);
                if (
                    (res.data.content || []).length === 0 &&
                    (appliedSearch || statusFilter || createdDate || reminderType)
                ) {
                    setError('No call queue items match your filters.');
                }
            })
            .catch(() => {
                setError('Failed to load call queue. Please try again.');
                setCurrentItems([]);
            })
            .finally(() => setIsLoading(false));
    }, [
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
        appliedSearch,
        statusFilter,
        createdDate,
        reminderType,
    ]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        setCurrentPage(0);
    }, [appliedSearch, createdDate, reminderType]);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleCheckboxChange = (event, item) => {
        if (event.target.checked) {
            setSelectedItems((prev) => [...prev, item]);
        } else {
            setSelectedItems((prev) => prev.filter((s) => s.id !== item.id));
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(currentItems);
        } else {
            setSelectedItems([]);
        }
    };

    const handleClearFilters = () => {
        onClearSearch?.();
        setStatusFilter('');
        setCreatedDate('');
        setReminderType('');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleDelete = async () => {
        if (selectedItems.length === 0) return;

        const count = selectedItems.length;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to delete ${count} selected item${count > 1 ? 's' : ''}. This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm',
                cancelButton: 'btn btn-secondary btn-sm',
                actions: 'gap-3',
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        const ids = selectedItems.map((item) => item.id);
        try {
            await processService.deleteCallQueue(ids);
            await Swal.fire({
                title: 'Deleted!',
                text: `${count} item${count > 1 ? 's have' : ' has'} been deleted successfully.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
                customClass: { title: 'card-title' },
                buttonsStyling: false,
            });
            setSelectedItems([]);
            if (currentPage === 0) {
                fetchList();
            } else {
                setCurrentPage(0);
            }
        } catch {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to delete selected items. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
        }
    };

    const handleResend = async (items = selectedItems) => {
        const resendable = items.filter((item) => canResendCallQueue(item.status));
        if (resendable.length === 0) {
            Swal.fire({
                title: 'Cannot resend',
                text: 'Selected items are not eligible for resend (Queued, Accepted, and Delivered are blocked).',
                icon: 'info',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
            return;
        }

        const count = resendable.length;
        const skipped = items.length - count;
        const result = await Swal.fire({
            title: 'Resend call?',
            text:
                `Publish ${count} call${count > 1 ? 's' : ''} directly to RabbitMQ for delivery.` +
                (skipped > 0 ? ` ${skipped} ineligible item${skipped > 1 ? 's' : ''} will be skipped.` : ''),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-telephone-outbound"></i> Yes, resend`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-primary btn-sm',
                cancelButton: 'btn btn-secondary btn-sm',
                actions: 'gap-3',
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        try {
            await processService.resendCallQueue(resendable.map((item) => item.id));
            await Swal.fire({
                title: 'Queued for resend',
                text: `${count} call${count > 1 ? 's have' : ' has'} been published to RabbitMQ.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
                customClass: { title: 'card-title' },
                buttonsStyling: false,
            });
            setSelectedItems([]);
            fetchList();
        } catch {
            Swal.fire({
                title: 'Resend failed',
                text: 'Could not resend selected calls. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
        }
    };

    const StatusBadge = ({ status, statusCode }) => {
        const key = status || (statusCode != null ? STATUS_OPTIONS[statusCode] : null);
        const badgeClass = CALL_STATUS_BADGES[key] || 'text-bg-secondary';
        return <span className={`badge ${badgeClass}`}>{formatStatusLabel(key)}</span>;
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = currentItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    return (
        <>
            <Collapse in={searchPanelOpen}>
                <div>
                    <ProcessQueueFilters
                        showSearch={false}
                        showCreatedDate
                        showReminderType
                        createdDate={createdDate}
                        onCreatedDateChange={(value) => {
                            setCreatedDate(value);
                            setCurrentPage(0);
                        }}
                        reminderType={reminderType}
                        onReminderTypeChange={(value) => {
                            setReminderType(value);
                            setCurrentPage(0);
                        }}
                        reminderTypeOptions={reminderTypeOptions}
                        statusFilter={statusFilter}
                        onStatusFilterChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(0);
                        }}
                        statusOptions={STATUS_OPTIONS}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onClear={handleClearFilters}
                        canClear={Boolean(
                            appliedSearch || statusFilter || searchTerm || createdDate || reminderType
                        )}
                    />
                </div>
            </Collapse>

            {selectedItems.length > 0 && (
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => handleResend()}>
                        <i className="bi bi-telephone-outbound me-1"></i>
                        Resend selected ({selectedItems.length})
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>
                        <i className="bi bi-trash me-1"></i>
                        Delete selected ({selectedItems.length})
                    </button>
                </div>
            )}

            {isLoading ? (
                <Loading />
            ) : error && currentItems.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={
                                            currentItems.length > 0 &&
                                            selectedItems.length === currentItems.length
                                        }
                                        onChange={handleSelectAll}
                                        aria-label="Select all rows"
                                    />
                                </th>
                                <th>
                                    <i className="bi bi-person-vcard"></i> Patient
                                </th>
                                <th>
                                    <i className="bi bi-bell"></i> Reminder Name
                                </th>
                                <th>
                                    <i className="bi bi-telephone"></i> Receiver
                                </th>
                                <th>
                                    <i className="bi bi-file-text"></i> Script
                                </th>
                                <th>
                                    <i className="bi bi-activity"></i> Status
                                </th>
                                <th>
                                    <i className="bi bi-calendar-event"></i> Scheduled
                                </th>
                                <th style={{ width: '56px' }} className="text-center">
                                    <i className="bi bi-gear"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-4">
                                        No call queue items found.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr
                                            className={`process-queue-row--clickable${
                                                expandedRowId === item.id ? ' table-active' : ''
                                            }`}
                                            onClick={() => toggleRow(item.id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    toggleRow(item.id);
                                                }
                                            }}
                                        >
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.some((s) => s.id === item.id)}
                                                    onChange={(e) => handleCheckboxChange(e, item)}
                                                />
                                            </td>
                                            <td>
                                                <div className="fw-medium small">{item.patientName || '—'}</div>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {item.reminderName ? toProperCase(item.reminderName) : '—'}
                                                </small>
                                            </td>
                                            <td>
                                                <small className="text-break">{item.receiver || '—'}</small>
                                            </td>
                                            <td>
                                                <small title={item.content}>{truncateText(item.content, 40)}</small>
                                            </td>
                                            <td>
                                                <StatusBadge status={item.status} statusCode={item.statusCode} />
                                            </td>
                                            <td>
                                                <small>
                                                    {formatScheduledSend(item.dateSend, item.timeSend)}
                                                </small>
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant={
                                                        expandedRowId === item.id
                                                            ? 'outline-secondary'
                                                            : 'outline-primary'
                                                    }
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleRow(item.id);
                                                    }}
                                                    title={expandedRowId === item.id ? 'Collapse' : 'Expand'}
                                                    aria-expanded={expandedRowId === item.id}
                                                >
                                                    <i
                                                        className={`bi bi-chevron-${
                                                            expandedRowId === item.id ? 'up' : 'down'
                                                        }`}
                                                    ></i>
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr className="p-0 border-top-0">
                                            <td colSpan={8} className="p-0 border-top-0">
                                                <Collapse in={expandedRowId === item.id}>
                                                    <div className="process-queue-detail p-3 bg-light border-top">
                                                        <div className="row">
                                                            <DetailField label="Queue ID" mono>
                                                                {item.id}
                                                            </DetailField>
                                                            <DetailField label="Clinic ID" mono>
                                                                <Link
                                                                    to={`/clinics/${item.clinicId}`}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    {item.clinicId}
                                                                </Link>
                                                            </DetailField>
                                                            <DetailField label="User ID" mono>
                                                                {item.userId}
                                                            </DetailField>
                                                            <DetailField label="Patient ID" mono>
                                                                {item.patientId}
                                                            </DetailField>
                                                            <DetailField label="Reminder Type ID" mono>
                                                                {item.reminderTypeId}
                                                            </DetailField>
                                                            <DetailField label="First / Last / Preferred">
                                                                {[item.firstName, item.lastName, item.preferredName]
                                                                    .filter(Boolean)
                                                                    .join(' · ') || '—'}
                                                            </DetailField>
                                                            <DetailField label="Sender">{item.sender}</DetailField>
                                                            <DetailField label="Receiver">{item.receiver}</DetailField>
                                                            <DetailField label="Priority">{item.priority}</DetailField>
                                                            <DetailField label="Voice">{item.voice}</DetailField>
                                                            <DetailField label="Language">{item.language}</DetailField>
                                                            <DetailField label="Interaction Type">
                                                                {item.interactionType}
                                                            </DetailField>
                                                            <DetailField label="Queue Status">
                                                                {item.queueStatus}
                                                            </DetailField>
                                                            <DetailField label="Provider Message ID" mono>
                                                                {item.providerMessageId}
                                                            </DetailField>
                                                            <DetailField label="Scheduled Date">
                                                                {item.dateSend}
                                                            </DetailField>
                                                            <DetailField label="Scheduled Time">
                                                                {item.timeSend}
                                                            </DetailField>
                                                            <DetailField label="Created At">{item.createdAt}</DetailField>
                                                            <DetailField label="Modified At">{item.modifiedAt}</DetailField>
                                                            <DetailField label="Created Server">
                                                                {item.createdServerId}
                                                            </DetailField>
                                                            <DetailField label="Modified Server">
                                                                {item.modifiedServerId}
                                                            </DetailField>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Call Script</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {item.content || '—'}
                                                                </pre>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <div className="text-muted small">Invalid Prompt</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {item.invalidPrompt || '—'}
                                                                </pre>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <div className="text-muted small">Timeout Prompt</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {item.timeoutPrompt || '—'}
                                                                </pre>
                                                            </div>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Options</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {formatExtraInfo(item.options)}
                                                                </pre>
                                                            </div>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Extra Info</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {formatExtraInfo(item.extraInfo)}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                        {canResendCallQueue(item.status) && (
                                                            <div className="mt-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => handleResend([item])}
                                                                >
                                                                    <i className="bi bi-telephone-outbound me-1"></i>
                                                                    Resend
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100, 150, 200].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default CallProcessTable;
